'use client';

import {
  useParams as useNextParams,
  useSearchParams as useNextSearchParams,
  usePathname,
  useRouter,
} from 'next/navigation';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * RouterShim provides react-router-dom compatible hooks using Next.js navigation.
 * This allows legacy components to work during the migration without changes.
 *
 * Usage:
 * 1. Wrap your route component with RouterShimProvider
 * 2. Legacy components using react-router-dom hooks will use these shims instead
 *
 * Note: This requires updating imports in legacy components from:
 *   import { useParams, useNavigate, ... } from 'react-router-dom';
 * To:
 *   import { useParams, useNavigate, ... } from '@/providers/RouterShim';
 *
 * Or use the babel/webpack alias approach to automatically redirect imports.
 */

// Params context for additional params not in the URL
interface ParamsContextValue {
  additionalParams?: Record<string, string>;
}

const ParamsContext = createContext<ParamsContextValue>({});

// Global blocker store for navigation interception
interface BlockerEntry {
  id: number;
  shouldBlock: (args: {
    currentLocation: BlockerLocation;
    nextLocation: BlockerLocation;
  }) => boolean;
  onBlock: () => void;
}

interface BlockerLocation {
  pathname: string;
  search: string;
  hash: string;
}

let blockerIdCounter = 0;
const activeBlockers = new Map<number, BlockerEntry>();
let pendingNavigation: { path: string; options?: { replace?: boolean; state?: unknown } } | null =
  null;

// Navigation state storage - simple in-memory Map (does not persist across refresh)
const navigationState = new Map<string, unknown>();

/**
 * Get all possible base paths for a pathname (for subroute support)
 * e.g., /a/b/c/d -> ['/a/b/c/d', '/a/b/c', '/a/b', '/a']
 */
function getPathHierarchy(pathname: string): string[] {
  const paths: string[] = [pathname];
  let current = pathname;
  while (current.length > 1) {
    const lastSlash = current.lastIndexOf('/');
    if (lastSlash <= 0) break;
    current = current.substring(0, lastSlash);
    paths.push(current);
  }
  return paths;
}

function setNavigationState(pathname: string, state: unknown) {
  if (state != null) {
    navigationState.set(pathname, state);
  }
}

function getNavigationState(pathname: string): unknown {
  // Check path and all parent paths (for subroute support)
  for (const path of getPathHierarchy(pathname)) {
    if (navigationState.has(path)) {
      return navigationState.get(path);
    }
  }
  return null;
}

function clearStateNotInPath(newPathname: string) {
  const hierarchy = getPathHierarchy(newPathname);
  for (const key of navigationState.keys()) {
    // Get parent of stored path (to allow sibling navigation to keep state)
    const parentPath = key.substring(0, key.lastIndexOf('/')) || '/';
    // Only clear if parent is not in new path's hierarchy (completely different area)
    if (!hierarchy.includes(parentPath)) {
      navigationState.delete(key);
    }
  }
}

function registerBlocker(
  shouldBlock: (args: {
    currentLocation: BlockerLocation;
    nextLocation: BlockerLocation;
  }) => boolean,
  onBlock: () => void,
): number {
  const id = ++blockerIdCounter;
  activeBlockers.set(id, { id, shouldBlock, onBlock });
  return id;
}

function unregisterBlocker(id: number): void {
  activeBlockers.delete(id);
}

function checkBlockers(
  currentLocation: BlockerLocation,
  nextLocation: BlockerLocation,
): BlockerEntry | null {
  for (const blocker of activeBlockers.values()) {
    if (blocker.shouldBlock({ currentLocation, nextLocation })) {
      return blocker;
    }
  }
  return null;
}

function setPendingNavigation(
  path: string,
  options?: { replace?: boolean; state?: unknown },
): void {
  pendingNavigation = { path, options };
}

function getPendingNavigation(): {
  path: string;
  options?: { replace?: boolean; state?: unknown };
} | null {
  return pendingNavigation;
}

function clearPendingNavigation(): void {
  pendingNavigation = null;
}

/**
 * Check for active blockers and trigger if navigation should be blocked.
 * Returns true if navigation was blocked, false if navigation can proceed.
 * Used by RelativeLink to intercept Link navigation.
 */
export function checkAndTriggerBlocker(
  currentLocation: BlockerLocation,
  nextLocation: BlockerLocation,
  pendingPath: string,
): boolean {
  const blocker = checkBlockers(currentLocation, nextLocation);
  if (blocker) {
    setPendingNavigation(pendingPath);
    blocker.onBlock();
    return true;
  }
  return false;
}

export type { BlockerLocation };

interface RouterShimProviderProps {
  children: ReactNode;
  additionalParams?: Record<string, string>;
}

/**
 * RouterShimProvider allows passing additional params that may not be in the URL
 */
export function RouterShimProvider({ children, additionalParams }: RouterShimProviderProps) {
  // Memoize the context value to prevent unnecessary re-renders of context consumers
  const contextValue = useMemo(() => ({ additionalParams }), [additionalParams]);
  return <ParamsContext.Provider value={contextValue}>{children}</ParamsContext.Provider>;
}

/**
 * useParams - react-router-dom compatible useParams using Next.js
 */
export function useParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): T {
  const nextParams = useNextParams();
  const { additionalParams } = useContext(ParamsContext);

  return useMemo(() => {
    const params: Record<string, string | undefined> = {};

    // Add Next.js params
    if (nextParams) {
      for (const [key, value] of Object.entries(nextParams)) {
        if (Array.isArray(value)) {
          params[key] = value.join('/');
        } else {
          params[key] = value;
        }
      }
    }

    // Add additional params (these override nextParams for catch-all routes)
    if (additionalParams) {
      Object.assign(params, additionalParams);
    }

    return params as T;
  }, [nextParams, additionalParams]);
}

/**
 * Actually perform navigation without blocker checks (used after proceed)
 */
function performNavigation(
  router: ReturnType<typeof useRouter>,
  path: string,
  options?: { replace?: boolean; state?: unknown },
) {
  // Extract hash from path before navigation
  const hashIndex = path.indexOf('#');
  const newHash = hashIndex !== -1 ? path.slice(hashIndex) : '';

  // Store navigation state before navigating (if provided)
  // Extract pathname from path (remove query string and hash)
  const pathOnly = path.split('?')[0].split('#')[0];
  if (options?.state != null) {
    setNavigationState(pathOnly, options.state);
  } else {
    // Clear state when navigating without state to a different path
    clearStateNotInPath(pathOnly);
  }

  if (options?.replace) {
    router.replace(path);
  } else {
    router.push(path);
  }

  // Notify hash change listeners with the new hash value
  // Use setTimeout to ensure the navigation has started
  setTimeout(() => notifyHashChange(newHash), 0);
}

/**
 * useNavigate - react-router-dom compatible useNavigate using Next.js
 */
export function useNavigate() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useNextSearchParams();

  return useMemo(() => {
    const navigate = (
      to: string | number | { search?: string; pathname?: string; hash?: string },
      options?: { replace?: boolean; state?: unknown; relative?: 'route' | 'path' },
    ) => {
      if (typeof to === 'number') {
        if (to === -1) {
          router.back();
        } else if (to === 1) {
          router.forward();
        } else {
          router.back();
        }
        return;
      }

      // Handle object-based navigation (react-router-dom To object)
      let resolvedPath: string;
      if (typeof to === 'object') {
        const targetPathname = to.pathname || pathname;
        const search = to.search || '';
        const hash = to.hash || '';
        resolvedPath = `${targetPathname}${search}${hash}`;
      } else {
        resolvedPath = to;

        // Handle relative path navigation (react-router-dom compatibility)
        // Automatically detect relative paths (starting with ./ or ../) or when relative option is set
        const isRelativePath =
          to.startsWith('./') || to.startsWith('../') || to === '.' || to === '..';
        if ((isRelativePath || options?.relative === 'path') && !to.startsWith('/')) {
          // Remove trailing slash from pathname
          const basePath = pathname.replace(/\/$/, '');

          if (to === '' || to === '.') {
            // Navigate to current path
            resolvedPath = basePath;
          } else if (to === '..') {
            // Navigate to parent
            const segments = basePath.split('/');
            segments.pop();
            resolvedPath = segments.join('/') || '/';
          } else if (to.startsWith('./')) {
            // Relative to current path - append to current pathname
            const relativePart = to.slice(2); // Remove './'
            resolvedPath = `${basePath}/${relativePart}`;
          } else if (to.startsWith('../')) {
            // Handle parent directory navigation
            const segments = basePath.split('/');
            const toSegments = to.split('/');
            for (const segment of toSegments) {
              if (segment === '..') {
                segments.pop();
              } else if (segment !== '.' && segment !== '') {
                segments.push(segment);
              }
            }
            resolvedPath = segments.join('/') || '/';
          } else if (options?.relative === 'path') {
            // Non-prefixed relative path with explicit relative option (sibling navigation)
            const segments = basePath.split('/');
            segments.pop(); // Remove current segment
            segments.push(to); // Add new segment
            resolvedPath = segments.join('/');
          }
        }
      }

      // Check for active blockers before navigating
      const currentLocation: BlockerLocation = {
        pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : '',
        hash: typeof window !== 'undefined' ? window.location.hash : '',
      };

      // Parse the resolved path to get nextLocation
      const [nextPathname, rest] = resolvedPath.split('?');
      const [nextSearch, nextHash] = (rest || '').split('#');
      const nextLocation: BlockerLocation = {
        pathname: nextPathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: nextHash ? `#${nextHash}` : '',
      };

      const blocker = checkBlockers(currentLocation, nextLocation);
      if (blocker) {
        // Store the pending navigation and trigger the blocker
        setPendingNavigation(resolvedPath, options);
        blocker.onBlock();
        return;
      }

      // No blockers, proceed with navigation
      performNavigation(router, resolvedPath, options);
    };

    return navigate;
  }, [router, pathname, searchParams]);
}

/**
 * Execute pending navigation (called when blocker proceeds)
 */
export function executePendingNavigation(router: ReturnType<typeof useRouter>) {
  const pending = getPendingNavigation();
  if (pending) {
    clearPendingNavigation();
    performNavigation(router, pending.path, pending.options);
  }
}

// Hash change notification system for client-side navigation
type HashChangeListener = (hash: string) => void;
const hashChangeListeners = new Set<HashChangeListener>();

function notifyHashChange(hash: string) {
  for (const listener of hashChangeListeners) {
    listener(hash);
  }
}

/**
 * useLocation - react-router-dom compatible useLocation using Next.js
 */
export function useLocation() {
  const pathname = usePathname();
  const searchParams = useNextSearchParams();

  // Track hash changes reactively
  const [hash, setHash] = useState(() =>
    typeof window !== 'undefined' ? window.location.hash : '',
  );

  // Get navigation state from in-memory store
  const [locationState, setLocationState] = useState<unknown>(() => {
    return getNavigationState(pathname);
  });

  // Track the pathname we've processed state for
  const processedPathRef = useRef<string>(pathname);

  useEffect(() => {
    // Update hash on mount in case it changed
    setHash(window.location.hash);

    const handleBrowserHashChange = () => {
      setHash(window.location.hash);
    };

    const handleManualHashChange = (newHash: string) => {
      setHash(newHash);
    };

    // Listen for browser-triggered hash changes
    window.addEventListener('hashchange', handleBrowserHashChange);
    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleBrowserHashChange);
    // Listen for manual hash change notifications from navigate()
    hashChangeListeners.add(handleManualHashChange);

    return () => {
      window.removeEventListener('hashchange', handleBrowserHashChange);
      window.removeEventListener('popstate', handleBrowserHashChange);
      hashChangeListeners.delete(handleManualHashChange);
    };
  }, []);

  // Update location state when pathname changes
  useEffect(() => {
    // Skip if we already processed this pathname
    if (processedPathRef.current === pathname) {
      return;
    }
    processedPathRef.current = pathname;

    // Get state for this path (checks path hierarchy for subroute support)
    setLocationState(getNavigationState(pathname));
  }, [pathname]);

  return useMemo(() => {
    const search = searchParams.toString();
    return {
      pathname,
      search: search ? `?${search}` : '',
      hash,
      state: locationState,
      key: 'default',
    };
  }, [pathname, searchParams, hash, locationState]);
}

/**
 * NavigateFunction type - react-router-dom compatible
 */
export type NavigateFunction = ReturnType<typeof useNavigate>;

type SetSearchParamsOptions = { replace?: boolean };
type SetSearchParams = (
  params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
  options?: SetSearchParamsOptions,
) => void;

/**
 * useSearchParams - react-router-dom compatible (returns tuple)
 */
export function useSearchParams(): [URLSearchParams, SetSearchParams] {
  const router = useRouter();
  const pathname = usePathname();
  const nextSearchParams = useNextSearchParams();

  const searchParams = useMemo(
    () => new URLSearchParams(nextSearchParams.toString()),
    [nextSearchParams],
  );

  // Use a ref to always have access to the latest searchParams without causing re-renders
  const searchParamsRef = useRef(nextSearchParams);
  useEffect(() => {
    searchParamsRef.current = nextSearchParams;
  }, [nextSearchParams]);

  // Stable setSearchParams function that doesn't change between renders
  const setSearchParams = useCallback<SetSearchParams>(
    (newParams, options) => {
      const currentParams = new URLSearchParams(searchParamsRef.current.toString());
      const params = typeof newParams === 'function' ? newParams(currentParams) : newParams;

      // Check if params actually changed to avoid unnecessary navigation
      const newSearch = params.toString();
      const currentSearch = searchParamsRef.current.toString();
      if (newSearch === currentSearch) {
        return; // No change, skip navigation
      }

      const url = newSearch ? `${pathname}?${newSearch}` : pathname;
      if (options?.replace) {
        router.replace(url);
      } else {
        router.push(url);
      }
    },
    [router, pathname],
  );

  return [searchParams, setSearchParams];
}

// Re-export LinkProps type for components that need it (uses RelativeLinkProps which omits 'as' to avoid styled-components conflict)
export type { RelativeLinkProps as LinkProps } from '@/components/RelativeLink';
// Re-export Link component adapter with relative path support
export { RelativeLink as Link } from '@/components/RelativeLink';

/**
 * matchPath - react-router-dom compatible matchPath
 * Matches a pathname against a pattern.
 */
export function matchPath(
  pattern: string,
  pathname: string,
): { path: string; pathname: string } | null {
  // Convert react-router pattern to regex
  // Handle :param and *
  const regexPattern = pattern
    .replace(/:[^/]+/g, '([^/]+)') // :param -> capture group
    .replace(/\*/g, '.*'); // * -> match any

  // Ensure the pattern matches the whole pathname
  const regex = new RegExp(`^${regexPattern}$`);

  if (regex.test(pathname)) {
    return { path: pattern, pathname };
  }

  return null;
}

// Outlet re-export from OutletProvider
export { Outlet, useOutletContext } from './OutletProvider';

/**
 * Blocker state types - mimics react-router-dom
 */
export type BlockerState = 'unblocked' | 'blocked' | 'proceeding';

export interface Blocker {
  state: BlockerState;
  proceed?: () => void;
  reset?: () => void;
}

type BlockerFunction = (args: {
  currentLocation: BlockerLocation;
  nextLocation: BlockerLocation;
}) => boolean;

/**
 * useBlocker - react-router-dom compatible useBlocker
 *
 * This implementation intercepts navigation via useNavigate and shows
 * a modal when navigation is blocked. It also handles browser events
 * (back, forward, close tab, refresh) via beforeunload.
 */
export function useBlocker(shouldBlock: boolean | BlockerFunction): Blocker {
  const [state, setState] = useState<BlockerState>('unblocked');
  const router = useRouter();
  const blockerIdRef = useRef<number | null>(null);

  // Convert boolean to function for consistent handling
  const shouldBlockFn = useMemo(() => {
    if (typeof shouldBlock === 'boolean') {
      return () => shouldBlock;
    }
    return shouldBlock;
  }, [shouldBlock]);

  // Register/unregister the blocker
  useEffect(() => {
    // Register the blocker
    blockerIdRef.current = registerBlocker(shouldBlockFn, () => {
      setState('blocked');
    });

    return () => {
      // Unregister on cleanup
      if (blockerIdRef.current !== null) {
        unregisterBlocker(blockerIdRef.current);
        blockerIdRef.current = null;
      }
    };
  }, [shouldBlockFn]);

  // Handle browser navigation (back, forward, close tab, refresh)
  useEffect(() => {
    // For beforeunload, we can only use boolean check (no nextLocation available)
    const shouldBlockBeforeUnload = typeof shouldBlock === 'boolean' ? shouldBlock : false;

    if (!shouldBlockBeforeUnload) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  const proceed = useCallback(() => {
    setState('proceeding');
    // Execute the pending navigation
    executePendingNavigation(router);
    // Reset state after navigation
    setState('unblocked');
  }, [router]);

  const reset = useCallback(() => {
    // Clear pending navigation and reset state
    clearPendingNavigation();
    setState('unblocked');
  }, []);

  return useMemo(
    () => ({
      state,
      proceed: state === 'blocked' ? proceed : undefined,
      reset: state === 'blocked' ? reset : undefined,
    }),
    [state, proceed, reset],
  );
}
